import { isNil } from "ramda";
import { removeAlert, RemoveAlertAction, showAlert, ShowAlertAction } from "../Store/Shared/actions";
import { v4 as uuidv4 } from 'uuid';
import { State } from "../Models/State";
import { ApplicationCustomerSummary } from "../Models/CustomerPortalUser";
import { CustomerRoles } from "./Constants";
import {hasValue} from "@amsanalytics/react-shared-library";

export const displayError = (error: string): ShowAlertAction => {
  return showAlert({
    type: 'error',
    message: error,
    id: uuidv4(),
  });
};
export const removeError = (alertId: string): RemoveAlertAction => {
  return removeAlert(alertId);
}

export const nullOrNumber = (val: string): number | null => {
  if (val.length === 0) {
    return null;
  }

  const ret = parseFloat(val);
  if (ret === undefined) {
    return null;
  }

  return ret;
};

export const noop = () => { };

export const isNumber = (n: string): boolean => {
  return !isNaN(parseFloat(n));
}

export const capitalizeFirstLetter = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

// capitalizes the first letter of each word in the given string.
export const capitalizeFirstLetterOfEachWord = (word: string): string => {
  return word.split(' ').map((w: string) => capitalizeFirstLetter(w)).join(' ');
}

export const splitCamelCaseWord = (word: string | null | undefined): string => {
  if (isNil(word)) {
    return '';
  }
  return word.trim().split(/(?=[A-Z])/).join(' ');
}

// Removes all hyphens from the given string.
export const removeHyphensFromString = (word: string): string => {
  return word.replace(/-/g, "");
}

// Returns the given state that matches the abbreviation, otherwise returns undefined. Case insensitive.
export const getStateByAbbreviation = (abbreviation: string, states: State[]): State | undefined => {
  return states.find((s: State) => s.abbreviation.toLowerCase() === abbreviation.toLowerCase());
}

// returns true if the two strings are equal, otherwise returns false. Case insensitive, trim whitespace.
export const areStringsEqual = (str1: string | null | undefined, str2: string | null | undefined): boolean => {
  // if both are null or undefined, return true
  if (isNil(str1) && isNil(str2)) {
    return true;
  }
  return str1?.trim()?.toUpperCase() === str2?.trim()?.toUpperCase();
}

// returns true if the given string is a GUID based on its length, otherwise returns false.
export const isGuid = (guid: string): boolean => {
  return guid.trim().length === 36;
}

export const formatPhoneNumber = (phoneNumberString: string): string => {
  var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return '';
}

// removes all non-numeric characters from the given string.
export const removeNonNumericCharacters = (str: string): string => {
  return str.replace(/\D/g, '');
}


function formatToICalDate(date: Date) {
  var year = date.getFullYear();
  var month = ('0' + (date.getMonth() + 1)).slice(-2);
  var day = ('0' + date.getDate()).slice(-2);
  var hours = ('0' + date.getHours()).slice(-2);
  var minutes = ('0' + date.getMinutes()).slice(-2);
  var seconds = ('0' + date.getSeconds()).slice(-2);

  return year + month + day + 'T' + hours + minutes + seconds;
}
export const createEventString = (startDate: Date, endDate: Date, title: string, description: string, url: string, location?: string): string =>  {
  const eventString = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:${formatToICalDate(startDate)}%0ADTEND:${formatToICalDate(endDate)}%0ASUMMARY:${title}%0ADESCRIPTION:${description}%0ALOCATION:${location}%0AEND:VEVENT%0AEND:VCALENDAR`
return eventString;
}

export const getUtcFormattedString = (date: Date): string => {
  return date.toISOString();
};

export const sortCustomers = (ac1: ApplicationCustomerSummary, ac2: ApplicationCustomerSummary) => {
  if (ac1.roleId === CustomerRoles.applicant) {
    return -1;
  }
  if (ac2.roleId === CustomerRoles.applicant) {
    return 1;
  }
  return 0;
}
export const isValidURL = (url: string): boolean => {
  if (hasValue(url) && /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g.test(url)) {
    return true;
  } else {
    return false;
  }
}

// helper function to decode the base64 encoded URL rest params
export const base64DecodeParams = (encodedString: string): string | undefined  => {
  let decodedString;

  // For browser environments
  if (typeof window !== 'undefined') {
    decodedString = atob(encodedString);
  }

  // For Node.js environments
  if (typeof Buffer !== 'undefined') {
    decodedString = Buffer.from(encodedString, 'base64').toString();
  }

  return decodedString ? JSON.parse(decodedString) : undefined;
}

// helper function to encode the params to base64.
// The deposit link's encodeDepositPageParameters/decodeDepositPageParameters (Components/Pages/DepositPage/depositPageUtils)
// produce and read this same format (base64 of a JSON string array) with their own btoa/atob calls, because this module
// pulls in the shared library, which cannot load under the CRA Jest runner. Keep the two formats identical.
export const base64EncodeParams = (params: string[]): string | undefined => {
  // Convert params to a string (JSON is a good format for complex data)
  const paramsString = JSON.stringify(params);

  // For browser environments
  if (typeof window !== 'undefined') {
    return btoa(paramsString);
  }

  // For Node.js environments
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(paramsString).toString('base64');
  }
  return undefined;
}

// returns true/false boolean for the given "true" or "false" string. if the string is null or undefined, returns undefined.
export const stringToBoolean = (str: string | null | undefined): boolean | undefined => {
  if (isNil(str)) {
    return undefined;
  }
  if (str.toUpperCase() === 'TRUE') {
    return true;
  } else if (str.toUpperCase() === 'FALSE') {
    return false;
  }
  return false;
}

/*
data:text/calendar;charset=utf8,BEGIN:VCALENDAR
VERSION%0A2.0
BEGIN%0AVEVENT
URL%0A#
DTSTART%0A2024-02-27T15:00:00.000Z
DTEND%0A2024-02-27T15:00:00.000Z
SUMMARY%0AUpcoming Appointment
DESCRIPTION%0AYou have an appointment scheduled With Olivia Flores
LOCATION%0ASOUTH OKC
END%0AVEVENT
END%0AVCALENDAR
*/
